// /vehicle — Тээврийн хэрэгслийн бүртгэл (Phase F4 fleet registry)
// Promoted from the Settings tab so fleet management has a dedicated route.

import {
  Badge,
  Box,
  Button,
  Checkbox,
  Group,
  Loader,
  Modal,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { PencilSimpleIcon, PlusIcon, TrashIcon, TruckIcon, WarningCircleIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useSWR, { mutate } from "swr";
import { VehicleApi } from "../../apis";
import type { ICreateVehicleInput, IVehicle, VehicleClass } from "../../apis/vehicle";
import { PageLayout } from "../../components/layout";
import { TableFrame } from "../../components/delivery/atoms";
import { DeliveryActionButton, DeliveryRowAction } from "../../components/delivery/action-controls";
import { useDeliveryActions } from "../../components/delivery/delivery-actions";
import { translateBackendError } from "../../components/delivery/translate-error";
import { useVehicleClassLabel } from "../../components/delivery-routes/vehicle-class";
import { usePermission } from "../../components/permission";
import RenderText from "../../components/ui/render-text";
import { message } from "../../utils/message";
import { DotPill } from "../../components/delivery/atoms";
import { DisabledReason, EmptyStateGuide, HelpButtonGroup, HelpIcon, helpText, nextStepToast, useHelpContext } from "@dehub/theme";
import { HelpColumnTitle, HelpNote } from "../../components/ui";

// @dehub/help key namespace of this screen (spec 12 §0).
const HELP = "order.vehicle";

const SWR_KEY = "settings.vehicle.list";

const VEHICLE_CLASSES: VehicleClass[] = ["MOTORCYCLE", "CAR", "VAN", "TRUCK_SMALL", "TRUCK_LARGE"];

type FormShape = {
  plateNumber: string;
  vehicleClass: VehicleClass;
  make: string;
  model: string;
  year: number | string;
  licenseExpiryDate: string | null;
  registrationDocUrl: string;
  lastMaintenanceDate: string | null;
  nextMaintenanceDueDate: string | null;
  maintenanceNotes: string;
  isActive: boolean;
  notes: string;
};

function fromVehicle(v?: IVehicle | null): FormShape {
  return {
    plateNumber           : v?.plateNumber ?? "",
    vehicleClass          : v?.vehicleClass ?? "CAR",
    make                  : v?.make ?? "",
    model                 : v?.model ?? "",
    year                  : v?.year ?? "",
    licenseExpiryDate     : v?.licenseExpiryDate ?? null,
    registrationDocUrl    : v?.registrationDocUrl ?? "",
    lastMaintenanceDate   : v?.lastMaintenanceDate ?? null,
    nextMaintenanceDueDate: v?.nextMaintenanceDueDate ?? null,
    maintenanceNotes      : v?.maintenanceNotes ?? "",
    isActive              : v?.isActive ?? true,
    notes                 : v?.notes ?? "",
  };
}

// Mantine v9 DatePickerInput already returns ISO "YYYY-MM-DD" strings.
function isoDateOrNull(d: string | null): string | null {
  return d && d.trim() ? d.slice(0, 10) : null;
}

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const t = new Date(`${iso}T00:00:00Z`).getTime();
  return Math.round((t - Date.now()) / (24 * 60 * 60_000));
}

function ExpiryBadge({ iso, warnDays }: { iso: string | null; warnDays: number }) {
  const { t } = useTranslation(["delivery"]);
  const days = daysUntil(iso);
  if (days === null) return <RenderText text="-" color="dimmed" />;
  const color = days < 0 ? "red" : days <= warnDays ? "orange" : "gray";
  const label =
    days < 0
      ? t("delivery:vehicle.expiry.overdue", { count: Math.abs(days) })
      : days <= warnDays
        ? t("delivery:vehicle.expiry.soon", { count: days })
        : t("delivery:vehicle.expiry.remaining", { count: days });
  return (
    <Badge variant="light" color={color} size="md" radius="sm">
      {iso} ({label})
    </Badge>
  );
}

function VehicleFormModal({
  opened,
  onClose,
  initial,
}: {
  opened: boolean;
  onClose: () => void;
  initial?: IVehicle | null;
}) {
  const { t } = useTranslation(["delivery", "common"]);
  const isEdit = !!initial;
  const [submitting, setSubmitting] = useState(false);

  const classOptions = VEHICLE_CLASSES.map((value) => ({
    value,
    label: t(`delivery:vehicle.class.${value}`),
  }));

  const form = useForm<FormShape>({
    initialValues: fromVehicle(initial),
    validate     : {
      plateNumber: (v) => (!v.trim() ? helpText(`${HELP}.validation.plate`) ?? t("delivery:vehicle.validation.plateRequired") : null),
    },
  });

  const onSubmit = async (values: FormShape) => {
    setSubmitting(true);
    try {
      const payload: ICreateVehicleInput = {
        plateNumber           : values.plateNumber.trim(),
        vehicleClass          : values.vehicleClass,
        make                  : values.make.trim() || null,
        model                 : values.model.trim() || null,
        year                  : typeof values.year === "number" && !Number.isNaN(values.year) ? values.year : null,
        licenseExpiryDate     : isoDateOrNull(values.licenseExpiryDate),
        registrationDocUrl    : values.registrationDocUrl.trim() || null,
        lastMaintenanceDate   : isoDateOrNull(values.lastMaintenanceDate),
        nextMaintenanceDueDate: isoDateOrNull(values.nextMaintenanceDueDate),
        maintenanceNotes      : values.maintenanceNotes.trim() || null,
        isActive              : values.isActive,
        notes                 : values.notes.trim() || null,
      };
      if (isEdit && initial) {
        await VehicleApi.update(initial.id, payload);
      } else {
        await VehicleApi.create(payload);
      }
      await mutate(SWR_KEY);
      nextStepToast(`${HELP}.toast.saved`, { fallback: { title: t("delivery:vehicle.messages.saved") } });
      onClose();
      form.reset();
    } catch (err: any) {
      message.error(translateBackendError(err, "error.vehicle.save_failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEdit ? t("delivery:vehicle.modal.editTitle") : t("delivery:vehicle.modal.createTitle")}
      size="md"
    >
      <Box component="form" onSubmit={form.onSubmit(onSubmit)}>
        <Stack gap="sm">
          <Group grow>
            <TextInput label={t("delivery:vehicle.fields.plateNumber")} required {...form.getInputProps("plateNumber")} />
            <Select
              label={<>{t("delivery:vehicle.fields.vehicleClass")}<HelpIcon helpKey={`${HELP}.types.vehicle-class`} /></>}
              data={classOptions}
              value={form.values.vehicleClass}
              onChange={(v) => form.setFieldValue("vehicleClass", (v as VehicleClass) ?? "CAR")}
            />
          </Group>
          <Group grow>
            <TextInput label={t("delivery:vehicle.fields.make")} {...form.getInputProps("make")} />
            <TextInput label={t("delivery:vehicle.fields.model")} {...form.getInputProps("model")} />
            <NumberInput label={t("delivery:vehicle.fields.year")} min={1900} max={2100} {...form.getInputProps("year")} />
          </Group>

          <DatePickerInput
            label={t("delivery:vehicle.fields.licenseExpiryDate")}
            valueFormat="YYYY-MM-DD"
            value={form.values.licenseExpiryDate}
            onChange={(v) => form.setFieldValue("licenseExpiryDate", v ?? null)}
            clearable
          />
          <TextInput label={t("delivery:vehicle.fields.registrationDocUrl")} {...form.getInputProps("registrationDocUrl")} />

          <Group grow>
            <DatePickerInput
              label={t("delivery:vehicle.fields.lastMaintenanceDate")}
              valueFormat="YYYY-MM-DD"
              value={form.values.lastMaintenanceDate}
              onChange={(v) => form.setFieldValue("lastMaintenanceDate", v ?? null)}
              clearable
            />
            <DatePickerInput
              label={t("delivery:vehicle.fields.nextMaintenanceDueDate")}
              valueFormat="YYYY-MM-DD"
              value={form.values.nextMaintenanceDueDate}
              onChange={(v) => form.setFieldValue("nextMaintenanceDueDate", v ?? null)}
              clearable
            />
          </Group>

          <Textarea label={t("delivery:vehicle.fields.maintenanceNotes")} autosize minRows={2} {...form.getInputProps("maintenanceNotes")} />
          <Textarea label={t("delivery:vehicle.fields.notes")} autosize minRows={2} {...form.getInputProps("notes")} />

          <Checkbox
            label={t("delivery:vehicle.fields.isActive")}
            checked={form.values.isActive}
            onChange={(e) => form.setFieldValue("isActive", e.currentTarget.checked)}
          />

          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={onClose}>{t("common:actions.dismiss")}</Button>
            <Button type="submit" loading={submitting}>
              {isEdit ? t("common:actions.save") : t("common:actions.create")}
            </Button>
          </Group>
        </Stack>
      </Box>
    </Modal>
  );
}

export function VehiclePage() {
  const { t } = useTranslation(["delivery", "common"]);
  // vehicle/list.ts and get.ts need BIZ_GOV_VIEW, create/update/remove need BIZ_GOV_MNG (O193 / O09).
  const BIZ_GOV_VIEW = usePermission("BIZ_GOV_VIEW");
  const { stateOf } = useDeliveryActions();
  const classLabel = useVehicleClassLabel();
  const createState = stateOf("vehicleCreate", {});
  const editState = stateOf("vehicleEdit", {});
  const deleteState = stateOf("vehicleDelete", {});

  const { data, isLoading } = useSWR(SWR_KEY, () => VehicleApi.list({ offset: { limit: 200, skip: 0 } }), { revalidateOnFocus: false });
  const rows = data?.rows ?? [];

  // @dehub/help: the drawer's «Одоо» card counts vehicles whose licence (30 days) or maintenance (7 days) is due.
  const expiringCount = rows.filter((v) => {
    const licence = daysUntil(v.licenseExpiryDate);
    const maintenance = daysUntil(v.nextMaintenanceDueDate);
    return (licence !== null && licence <= 30) || (maintenance !== null && maintenance <= 7);
  }).length;
  const { set: setHelpContext } = useHelpContext();
  useEffect(() => {
    setHelpContext({ vars: { expiringCount, total: rows.length } });
  }, [expiringCount, rows.length, setHelpContext]);

  const [editTarget, setEditTarget] = useState<IVehicle | null>(null);
  const [createOpen, { open: openCreate, close: closeCreate }] = useDisclosure(false);
  const [editOpen, { open: openEdit, close: closeEdit }] = useDisclosure(false);

  const handleEdit = (v: IVehicle) => { setEditTarget(v); openEdit(); };
  const handleCreate = () => { if (createState.allowed) openCreate(); };

  const handleDelete = (v: IVehicle) => {
    // Ord_DeliveryModals 5: 440 dialog, the vehicle on a card, a neutral delete button.
    modals.openConfirmModal({
      size        : 440,
      title       : (
        <Box>
          <Text fw={650} fz="var(--nw-fz-drawer)">{t("delivery:vehicle.delete.title")}</Text>
          <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">{t("delivery:vehicle.delete.subtitle")}</Text>
        </Box>
      ),
      children    : (
        <Stack gap="md">
          <Box p="md" bg="gray.0" bd="1px solid var(--nw-table-border)" bdrs="md">
            <Text fw={700} ff="monospace">{v.plateNumber}</Text>
            <Text size="sm" c="gray.8">
              {[[v.make, v.model].filter(Boolean).join(" "), classLabel(v.vehicleClass)].filter(Boolean).join(" · ")}
            </Text>
          </Box>
          <Text size="sm">{t("delivery:vehicle.delete.body")}</Text>
          <HelpNote helpKey={`${HELP}.confirm.delete`} mt={0} />
        </Stack>
      ),
      labels      : { confirm: t("common:actions.delete"), cancel: t("common:actions.dismiss") },
      confirmProps: { color: "gray", variant: "light" },
      onConfirm   : async () => {
        try {
          await VehicleApi.remove(v.id);
          await mutate(SWR_KEY);
          nextStepToast(`${HELP}.toast.deleted`, { fallback: { title: t("delivery:vehicle.messages.deleted") } });
        } catch (err: any) {
          message.error(translateBackendError(err, "error.vehicle.delete_failed"));
        }
      },
    });
  };

  const breadcrumbs = [
    { to: "/", label: t("common:breadcrumb.home") },
    { label: t("delivery:vehicle.breadcrumb") },
  ];

  return (
    <PageLayout
      icon={<TruckIcon size={22} />}
      title={t("delivery:vehicle.title")}
      subTitle={t("delivery:vehicle.subtitle")}
      breadcrumb={breadcrumbs}
      extra={
        BIZ_GOV_VIEW.hasAccess
          ? [
              // @dehub/help: attached «?» (R2) + reason popover while the write permission is missing (R4).
              <DisabledReason key="new" helpKey={`${HELP}.reason.permission`} reason="new" when={!createState.allowed} position="bottom-end">
                <HelpButtonGroup helpKey={`${HELP}.action.new`}>
                  <DeliveryActionButton data-help-id="vehicle-new" state={createState} color="dark" leftSection={<PlusIcon size={16} />} onClick={handleCreate}>
                    {t("delivery:vehicle.buttons.new")}
                  </DeliveryActionButton>
                </HelpButtonGroup>
              </DisabledReason>,
            ]
          : undefined
      }
    >
      {BIZ_GOV_VIEW.hasAccess ? (
        <Box>
          <Group justify="space-between" mb="md">
            <Stack gap={2}>
              <Text fw={600} size="lg">
                {t("delivery:vehicle.section.title")}
                <HelpIcon helpKey={`${HELP}.section.registry`} variant="section" />
              </Text>
              <Text size="sm" c="dimmed">
                {t("delivery:vehicle.section.description")}
              </Text>
            </Stack>
          </Group>

          {isLoading ? (
            <Group justify="center" py="lg"><Loader size="sm" /></Group>
          ) : rows.length === 0 ? (
            <EmptyStateGuide
              helpKey={`${HELP}.empty.main`}
              actions={{ create: handleCreate }}
              fallback={
                <Group justify="center" py="lg" gap="xs">
                  <WarningCircleIcon size={18} color="var(--mantine-color-dimmed)" />
                  <Text size="sm" c="dimmed">{t("delivery:vehicle.empty")}</Text>
                </Group>
              }
            />
          ) : (
            <TableFrame>
            <Table highlightOnHover>
              <Table.Thead bg="var(--nw-table-head-bg)">
                <Table.Tr>
                  <Table.Th w={120}>{t("common:actions.action")}</Table.Th>
                  <Table.Th>{t("delivery:vehicle.table.plate")}</Table.Th>
                  <Table.Th><HelpColumnTitle helpKey={`${HELP}.field.col-class`}>{t("delivery:vehicle.table.vehicleClass")}</HelpColumnTitle></Table.Th>
                  <Table.Th>{t("delivery:vehicle.table.makeModel")}</Table.Th>
                  <Table.Th><HelpColumnTitle helpKey={`${HELP}.field.col-license`}>{t("delivery:vehicle.table.license")}</HelpColumnTitle></Table.Th>
                  <Table.Th><HelpColumnTitle helpKey={`${HELP}.field.col-maintenance`}>{t("delivery:vehicle.table.maintenance")}</HelpColumnTitle></Table.Th>
                  <Table.Th>{t("delivery:vehicle.table.active")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((v) => (
                  <Table.Tr key={v.id}>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        <DeliveryRowAction
                          label={t("delivery:vehicle.tooltip.edit")}
                          icon={<PencilSimpleIcon />}
                          state={editState}
                          onClick={() => handleEdit(v)}
                        />
                        <DeliveryRowAction
                          label={t("delivery:vehicle.tooltip.delete")}
                          icon={<TrashIcon />}
                          state={deleteState}
                          onClick={() => handleDelete(v)}
                        />
                      </Group>
                    </Table.Td>
                    <Table.Td><Text ff="monospace" fz="var(--nw-fz-mono)" fw={650} translate="no">{v.plateNumber || "-"}</Text></Table.Td>
                    <Table.Td>
                      <Text size="sm">{classLabel(v.vehicleClass)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <RenderText text={[v.make, v.model].filter(Boolean).join(" ") || "-"} />
                      {v.year && <RenderText text={v.year} size="sm" color="dimmed" />}
                    </Table.Td>
                    <Table.Td><ExpiryBadge iso={v.licenseExpiryDate} warnDays={30} /></Table.Td>
                    <Table.Td><ExpiryBadge iso={v.nextMaintenanceDueDate} warnDays={7} /></Table.Td>
                    <Table.Td>
                      <DotPill tone={v.isActive ? "green" : "grey"}>
                        {v.isActive ? t("common:status.active") : t("common:status.inactive")}
                      </DotPill>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            </TableFrame>
          )}

          {(!editState.allowed || !deleteState.allowed) && (
            <Text size="sm" c="dimmed" mt="sm">
              {t("delivery:vehicle.section.disabledNote", { reason: editState.reason ?? deleteState.reason })}
            </Text>
          )}

          <VehicleFormModal opened={createOpen} onClose={closeCreate} />
          <VehicleFormModal opened={editOpen} onClose={closeEdit} initial={editTarget} key={`v-edit-${editTarget?.id ?? "none"}`} />
        </Box>
      ) : (
        BIZ_GOV_VIEW.accessDenied()
      )}
    </PageLayout>
  );
}

export default VehiclePage;
